import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { UserAccountModel, LookupItem, AccountUpdateRequest } from '../../models/account.model';
import { NotificationService } from '../../../core/services/notification.service';
import {
  AccountFormConfig,
  DEFAULT_LANGUAGES,
  DEFAULT_SHORT_TIMES,
} from '../../configuration/account-form.config';
import { getValidationMessage, VALIDATION_MESSAGES } from '../../constant/validation-messages';
import { AccountService } from '../../services/account.service';
import { DynamicFormComponent } from '../forms/dynamic-form/dynamic-form.component';
import {
  DynamicFormValue,
  DynamicFormConfig,
  DynamicFormState,
} from '../forms/dynamic-form/models/dynamic-form.model';

@Component({
  selector: 'app-account-form',
  standalone: true,
  imports: [CommonModule, DynamicFormComponent],
  templateUrl: './account-form.component.html',
  styleUrl: './account-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFormComponent implements OnInit, OnDestroy {
  @Input() accountData?: UserAccountModel;

  @Output() formSubmit = new EventEmitter<DynamicFormValue>();
  @Output() formCancel = new EventEmitter<void>();

  @ViewChild('dynamicForm') dynamicForm!: DynamicFormComponent;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly accountService = inject(AccountService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();

  dialogRef?: DialogRef;

  formConfig!: DynamicFormConfig;
  initialValues: DynamicFormValue = {};
  isDialogMode = false;
  isLoading = false;

  currentFormState: DynamicFormState = {
    valid: false,
    dirty: false,
    touched: false,
    value: {},
    errors: null,
  };

  timezones: LookupItem[] = [];

  private userId = 0;

  get canSave(): boolean {
    return !this.isLoading && this.currentFormState.valid;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    const dialogData = this.dialogRef?.content?.instance?.data;
    this.userId = dialogData?.userId ?? 0;

    this.loadAccountData();
  }

  private loadAccountData(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.accountService
      .getAccountById(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.messageType === 0 && response?.data) {
            this.accountData = response.data;
            this.timezones = response.data.timeZones ?? [];

            this.loadFormConfig();
            this.setInitialValues(response.data);
          } else {
            this.notificationService.error(
              getValidationMessage(response?.message, VALIDATION_MESSAGES.FAILED_TO_LOAD_DATA)
            );
          }

          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading account data:', error);
          this.notificationService.error(VALIDATION_MESSAGES.FAILED_TO_LOAD_DATA);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadFormConfig(): void {
    this.formConfig = AccountFormConfig.getEditConfig(
      this.timezones,
      DEFAULT_LANGUAGES,
      DEFAULT_SHORT_TIMES
    );
    this.formConfig.mode = this.isDialogMode ? 'popup' : 'page';
    this.formConfig.showActions = false;
  }

  private setInitialValues(account: UserAccountModel): void {
    this.initialValues = {
      id: account.id,
      firstName: account.firstName,
      lastName: account.lastName,
      email: account.email,
      language: 1, // default english
      timeZone: account.timeZone,
      shortTime: account.shortTime,
    };
  }

  handleFormStateChange(state: DynamicFormState): void {
    this.currentFormState = state;
  }

  handleFormSubmit(formValue: DynamicFormValue): void {
    if (this.isLoading) {
      return;
    }

    this.saveAccount(formValue);
  }

  private saveAccount(formValue: DynamicFormValue): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const payload = this.preparePayload(formValue);

    this.accountService
      .updateAccount(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          const messageType = response?.messageType;

          if (messageType === 0) {
            this.notificationService.success('Account updated successfully');
            this.formSubmit.emit(formValue);

            if (this.dialogRef) {
              this.dialogRef.close({
                success: true,
                action: 'save',
                data: response?.data ?? payload,
              });
            }
          } else {
            this.notificationService.error(VALIDATION_MESSAGES.FAILED_TO_SAVE_DATA);
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading = false;
          const errorMessage = error?.error?.message ?? VALIDATION_MESSAGES.FAILED_TO_SAVE_DATA;
          this.notificationService.error(errorMessage);
          this.cdr.markForCheck();
        },
      });
  }

  handleFormCancel(): void {
    this.formCancel.emit();

    if (this.dialogRef) {
      this.dialogRef.close({ success: false, action: 'cancel' });
    }
  }

  private preparePayload(formValue: DynamicFormValue): AccountUpdateRequest {
    return {
      id: this.accountData?.id ?? this.userId,
      firstName: formValue['firstName'] as string,
      lastName: formValue['lastName'] as string,
      email: formValue['email'] as string,
      language: formValue['language'] as number | null,
      timeZone: formValue['timeZone'] as number | null,
      shortTime: formValue['shortTime'] as number | null,
    };
  }

  onSubmit(): void {
    if (this.dynamicForm && !this.isLoading) {
      this.dynamicForm.onSubmit();
    }
  }

  isFormDirty(): boolean {
    return this.currentFormState?.dirty ?? false;
  }

  isFormValid(): boolean {
    return this.currentFormState?.valid ?? false;
  }

  resetForm(): void {
    if (this.dynamicForm) {
      this.dynamicForm.reset();
    }
  }

  validateForm(): boolean {
    if (this.dynamicForm) {
      this.dynamicForm.validateAllFields();
      return this.dynamicForm.isValid();
    }
    return false;
  }

  getFormValue(): DynamicFormValue {
    return this.dynamicForm ? this.dynamicForm.getValue() : {};
  }
}
